"use client";
import React, { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import { salesContacts } from "@/data/salesContacts";
import { useHomeLanguage } from "./HomeLanguageContext";

const contactInfo = [
  {
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
    label: "Alamat Kantor",
    value: "18 Office Park Building, Jl. TB Simatupang No.Kav. 18, 21st Floor Unit C, Jakarta Selatan 12520",
  },
  {
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
    label: "Email",
    value: "info@langitcreative.solutions",
    href: "mailto:info@langitcreative.solutions",
  },
  {
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.6 19.79 19.79 0 0 1 1.61 5c-.01-1.1.84-2 1.94-2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 10a16 16 0 0 0 6 6l.9-.9a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21.5 17z"/></svg>,
    label: "Telepon",
    value: "+62 821-2588-6584",
    href: "tel:+6282125886584",
  },
];

export default function ContactSection() {
  const { lang } = useHomeLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.08 }
    );
    sectionRef?.current?.querySelectorAll(".animate-on-scroll")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const isId = lang === "id";
  const localizedContactInfo = contactInfo.map((item) => {
    if (item.label === "Alamat Kantor") {
      return { ...item, label: isId ? "Alamat Kantor" : "Office Address" };
    }
    if (item.label === "Telepon") {
      return { ...item, label: isId ? "Telepon" : "Phone" };
    }
    return item;
  });

  return (
    <section id="contact" className="py-24 lg:py-28 bg-white relative overflow-hidden" ref={sectionRef}>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-primary/3 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-secondary/3 rounded-full blur-3xl" />
        <div className="absolute inset-0 grid-pattern opacity-15" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Left */}
          <div className="animate-on-scroll">
            <span className="section-label mb-6 inline-flex">
              <span className="w-1.5 h-1.5 rounded-full bg-primary pulse-dot inline-block" />
              {isId ? "Hubungi Kami" : "Contact us"}
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-fg leading-[1.1] tracking-[-0.03em] mb-4 mt-5">
              {isId ? "Mari Terhubung" : "Let's get in touch"}
            </h2>
            <p className="text-fg-muted leading-relaxed mb-7 text-[15px]">
              {isId ? "Kami akan membalas pesan Anda secepat mungkin." : "We will reply to your message as quickly as possible."}
            </p>
            <div className="grid sm:grid-cols-2 gap-2 text-[12px] text-fg-muted mb-6">
              <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{isId ? "Konsultasi kebutuhan bisnis" : "Business needs consultation"}</p>
              <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{isId ? "Saran modul paling relevan" : "Most relevant module recommendation"}</p>
              <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{isId ? "Estimasi implementasi" : "Implementation estimation"}</p>
              <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{isId ? "Pendampingan onboarding awal" : "Initial onboarding assistance"}</p>
            </div>

            <div className="space-y-3 mb-8 stagger-children">
              {localizedContactInfo?.map((info, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 p-5 rounded-xl bg-white border border-stone-100 shadow-card spotlight-card bento-card group"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center text-primary shrink-0 group-hover:bg-primary/12 transition-colors duration-300">
                    {info?.icon}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-fg/40 uppercase tracking-[0.14em] mb-1">{info?.label}</p>
                    {info?.href ? (
                      <a href={info?.href} className="text-fg font-medium text-sm hover:text-primary transition-colors leading-relaxed">
                        {info?.value}
                      </a>
                    ) : (
                      <p className="text-fg font-medium text-sm leading-relaxed">{info?.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Social */}
            <div className="flex items-center gap-3">
              <a
                href="https://instagram.com/kolabo.id"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white border border-stone-200 text-fg-muted hover:text-fg hover:border-primary/25 hover:bg-primary/4 transition-all text-sm font-semibold shadow-sm group"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/>
                  <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
                </svg>
                @kolabo.id
              </a>
            </div>
          </div>

          {/* Right: Sales team + CTA */}
          <div className="animate-on-scroll space-y-4">
            {/* Sales team card */}
            <div className="p-8 rounded-2xl bg-white relative overflow-hidden border border-stone-200 shadow-card">
              <div className="absolute top-0 right-0 w-56 h-56 bg-primary/8 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-secondary/6 rounded-full blur-3xl pointer-events-none" />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-cta pulse-dot" />
                  <span className="text-cta text-xs font-bold uppercase tracking-[0.18em]">{isId ? "Online Sekarang" : "Online Now"}</span>
                </div>
                <h3 className="font-heading text-lg font-bold text-fg mb-2 tracking-tight">{isId ? "Tim Sales Kolabo" : "Kolabo Sales Team"}</h3>
                <p className="text-fg-muted text-sm mb-6 leading-relaxed">{isId ? "Chat langsung untuk konsultasi gratis dan demo produk." : "Chat directly for a free consultation and product demo."}</p>
                <div className="space-y-2.5">
                  {salesContacts?.map((person, i) => (
                    <a
                      key={i}
                      href={`https://wa.me/${person?.phone?.replace(/[^0-9]/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-4 rounded-xl bg-stone-50 border border-stone-200 hover:bg-primary/5 hover:border-primary/25 hover:scale-[1.01] transition-all duration-300 group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden border border-stone-200 shadow-sm">
                          <AppImage
                            src={person?.avatar}
                            alt={person?.name}
                            width={40}
                            height={40}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <p className="text-fg font-semibold text-sm tracking-tight">{person?.name}</p>
                          <p className="text-fg-muted text-xs flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            {person?.role} ({isId ? person?.status : "Online"})
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-cta text-sm font-bold group-hover:gap-3 transition-all">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        {isId ? "Chat" : "Chat"}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Final CTA */}
            <div className="p-8 rounded-2xl bg-white border border-stone-200 shadow-card relative overflow-hidden group">
              <div className="absolute inset-0 opacity-10 pointer-events-none noise-overlay" />
              <div className="absolute top-0 right-0 w-40 h-40 bg-primary/10 rounded-full blur-2xl pointer-events-none" />
              <div className="relative z-10 text-center">
                <h3 className="font-heading text-2xl font-bold text-fg mb-2 tracking-tight">{isId ? "Siap Memulai?" : "Ready to Start?"}</h3>
                <p className="text-fg-muted text-sm mb-6 leading-relaxed">
                  {isId ? "Coba Kolabo gratis sekarang. Tidak perlu kartu kredit." : "Try Kolabo for free now. No credit card required."}
                </p>
                <a
                  href="#daftar"
                  className="inline-flex items-center gap-2.5 btn-gradient text-white font-semibold px-8 py-3.5 rounded-lg shadow-brand hover:shadow-brand-lg hover:-translate-y-0.5 hover:scale-105 transition-all duration-300 text-sm tracking-wide"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                  </svg>
                  {isId ? "Coba Gratis, Tanpa Ribet!" : "Try Free, No Hassle!"}
                </a>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-card bg-white p-3">
              <iframe
                title="Kolabo office map"
                className="w-full h-[240px] rounded-xl border-0"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3965.8079523543192!2d106.82310647480372!3d-6.28895539369998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e69f32e324b7aeb%3A0x2790eb242807a989!2sLangit%20Creative%20Solutions%20%7C%20PT.%20Langit%20Anantara%20Kreasi!5e0!3m2!1sen!2sid!4v1717047177541!5m2!1sen!2sid"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
